import { Host, h } from "@stencil/core";
import { SyncWithRAF } from "../../common/sync-with-frames";
import { analyzeLabelExistence, getElementInternalsLabel } from "../../common/analysis/accessibility";
const DEFAULT_PERCENTAGE_VALUE_WHEN_MIN_EQUALS_MAX = 0;
/**
 * The slider control is a input where the user selects a value from within a given range.
 *
 * @part track - The track of the slider element.
 * @part thumb - The thumb of the slider element.
 *
 * @part track__selected - Represents the portion of the track that is selected, that is, the portion of the track that starts at the min value and goes to the current value.
 * @part track__unselected - Represents the portion of the track that is not selected, that is, the portion of the track that starts at the current value and goes to the max value.
 *
 * @part disabled - Present in all parts when the control is disabled (`disabled` === `true`).
 */
export class ChSlider {
    constructor() {
        this.accessibleName = undefined;
        this.disabled = false;
        this.maxValue = 5;
        this.minValue = 0;
        this.showValue = false;
        this.step = 1;
        this.value = 0;
    }
    #accessibleNameFromExternalLabel;
    #lastModifiedValue = 0;
    #valuePositionRAF = new SyncWithRAF();
    internals;
    el;
    valueChanged(newValue) {
        // Update form value
        this.internals.setFormValue(newValue.toString());
    }
    /**
     * The `change` event is emitted when a change to the element's value is
     * committed by the user.
     */
    change;
    /**
     * The `input` event is fired synchronously when the value is changed.
     */
    input;
    #handleChange = (event) => {
        event.stopPropagation();
        const value = event.target.value;
        // Toggle the value property
        this.value = Number(value);
        this.change.emit(this.value);
    };
    #handleInput = (event) => {
        event.stopPropagation();
        // Update the last value modified by the user interaction
        this.#lastModifiedValue = Number(event.target.value);
        this.#valuePositionRAF.perform(this.#updateValue);
    };
    #updateValue = () => {
        this.value = this.#lastModifiedValue;
        this.input.emit(this.#lastModifiedValue);
    };
    #ensureValueIsInBetween = (minValue, value, maxValue) => Math.min(Math.max(minValue, value), maxValue);
    #getValuePercentage = (minValue, value, maxValue) => {
        const selectedValue = (value - minValue) / (maxValue - minValue);
        return selectedValue * 100;
    };
    disconnectedCallback() {
        this.#valuePositionRAF.cancel();
    }
    connectedCallback() {
        // Set form value
        this.internals.setFormValue(this.value.toString());
        // Accessibility
        this.internals.setFormValue(this.value?.toString());
        const labels = this.internals.labels;
        this.#accessibleNameFromExternalLabel = getElementInternalsLabel(labels);
        // Report any accessibility issue
        analyzeLabelExistence(this.el, "ch-slider", labels, this.#accessibleNameFromExternalLabel, this.accessibleName);
    }
    render() {
        const actualMaxValue = Math.max(this.minValue, this.maxValue);
        const actualValue = this.#ensureValueIsInBetween(this.minValue, this.value, actualMaxValue);
        const valueInPercentage = this.minValue < actualMaxValue
            ? this.#getValuePercentage(this.minValue, actualValue, actualMaxValue)
            : DEFAULT_PERCENTAGE_VALUE_WHEN_MIN_EQUALS_MAX;
        return (h(Host, { key: '2d95538328994b1ad85e928353a33a9b2dceff3c' }, h("div", { key: '93b0edf4260998cae711bc2a5675102b34476f50', class: "position-absolute-wrapper" }, h("input", { key: '92f7a087bc18bc6cc44bd3a14b82e7d5852d382d', "aria-label": this.#accessibleNameFromExternalLabel ?? this.accessibleName, class: "slider", disabled: this.disabled, type: "range", min: this.minValue, max: actualMaxValue, step: this.step, value: actualValue, onChange: !this.disabled ? this.#handleChange : undefined, onInput: !this.disabled ? this.#handleInput : undefined }), h("div", { key: '3b9a3d314d72b9d8f3f2fc0528d1086fbb8aac32', class: "track", part: `track${this.disabled ? " disabled" : ""}`, "aria-hidden": "true" }, h("div", { key: '931139033467c23290a3b7c9bd0877e36dcc40f8', class: "track__selected", part: `track__selected${this.disabled ? " disabled" : ""}`, style: { "--slider-selected-value": `${valueInPercentage}%` } }), h("div", { key: 'c23792da56085bd68f1fbacced9a03374ae563f9', class: "track__unselected", part: `track__unselected${this.disabled ? " disabled" : ""}`, style: {
                "--slider-unselected-value": `${100 - valueInPercentage}%`
            } })), h("div", { key: '86dd6642a32b9345d80b90feffa8b4f1a2de8422', class: "thumb", part: `thumb${this.disabled ? " disabled" : ""}`, style: {
                "--slider-thumb-position": `${valueInPercentage}%`,
                "--slider-value": `${valueInPercentage / 100}`
            } }))));
    }
    static get is() { return "ch-slider"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["slider.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["slider.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute allows you specify if the element is disabled.\nIf disabled, it will not trigger any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "maxValue": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify maximum value of the slider."
                },
                "attribute": "max-value",
                "reflect": false,
                "defaultValue": "5"
            },
            "minValue": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify minimum value of the slider."
                },
                "attribute": "min-value",
                "reflect": false,
                "defaultValue": "0"
            },
            "showValue": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you indicate whether the control should display a\nbubble with the current value upon interaction."
                },
                "attribute": "show-value",
                "reflect": false,
                "defaultValue": "false"
            },
            "step": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify the step of the slider.\n\nThis attribute is useful when the values of the slider can only take some\ndiscrete values. For example, if valid values are `[10, 20, 30]` set the\n`minValue` to `10`, the maxValue to `30`, and the step to `10`. If the\nstep is `0`, the any intermediate value is valid."
                },
                "attribute": "step",
                "reflect": false,
                "defaultValue": "1"
            },
            "value": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value of the control."
                },
                "attribute": "value",
                "reflect": false,
                "defaultValue": "0"
            }
        };
    }
    static get events() {
        return [{
                "method": "change",
                "name": "change",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "The `change` event is emitted when a change to the element's value is\ncommitted by the user."
                },
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                }
            }, {
                "method": "input",
                "name": "input",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "The `input` event is fired synchronously when the value is changed."
                },
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "value",
                "methodName": "valueChanged"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
